import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Globe, Instagram, Facebook, Music2, Star, Heart, Camera } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { SessionCard } from "../components/SessionCard";
import { toast } from "../components/ui/sonner";
import { mediaUrl } from "../lib/mediaUrl";
import { normalizeUrl, socialUrl } from "../lib/urlUtils";

export default function PhotographerProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [photographer, setPhotographer] = useState(null);
  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    api
      .get(`/photographers/${slug}`)
      .then(({ data }) => setPhotographer(data))
      .catch(() => setPhotographer(false));
  }, [slug]);

  const toggleFavorite = async () => {
    if (!user || user.role !== "stranger") return toast.error("Log in as a Stranger to save favorite photographers.");
    const { data } = await api.post(`/photographers/${slug}/favorite`);
    setFavorited(data.favorited);
    toast.success(data.favorited ? "Added to favorites" : "Removed from favorites");
  };

  if (photographer === null) return <div className="py-32 text-center font-medium" data-testid="photographer-loading">Loading photographer...</div>;
  if (photographer === false) return <div className="py-32 text-center font-medium" data-testid="photographer-not-found">Photographer not found.</div>;

  return (
    <div data-testid="photographer-profile-page">
      <section className="bg-midnight-black py-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center sm:items-start gap-8">
          <img src={mediaUrl(photographer.profile_photo_url)} alt={photographer.business_name} className="w-32 h-32 rounded-full object-cover border-2 border-stranger-orange" />
          <div className="text-center sm:text-left">
            <h1 className="text-clean-white font-extrabold uppercase text-2xl sm:text-4xl tracking-tight" data-testid="photographer-business-name">
              {photographer.business_name}
            </h1>
            <p className="text-clean-white/70 font-medium mt-2 flex items-center gap-2 justify-center sm:justify-start">
              <MapPin className="w-4 h-4 text-stranger-orange" /> {photographer.location} · {photographer.service_area}
            </p>
            <div className="flex items-center gap-3 mt-4 justify-center sm:justify-start flex-wrap">
              {photographer.verified && <VerifiedBadge />}
              {photographer.rating_avg && (
                <span className="flex items-center gap-1 text-clean-white/80 text-sm font-bold">
                  <Star className="w-4 h-4 text-stranger-orange fill-stranger-orange" /> {photographer.rating_avg} ({photographer.reviews.length})
                </span>
              )}
              <span className="flex items-center gap-1 text-clean-white/80 text-sm font-bold" data-testid="photographer-sessions-hosted">
                <Camera className="w-4 h-4 text-stranger-orange" /> {photographer.sessions_hosted_count} Stranger Session{photographer.sessions_hosted_count === 1 ? "" : "s"} hosted
              </span>
              <button onClick={toggleFavorite} data-testid="favorite-photographer-button" className="flex items-center gap-1.5 text-sm font-bold text-clean-white/80 border border-white/20 rounded-full px-4 py-1.5 hover:border-stranger-orange hover:text-stranger-orange transition-colors">
                <Heart className={`w-4 h-4 ${favorited ? "fill-stranger-orange text-stranger-orange" : ""}`} /> Save
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-clean-white py-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="md:col-span-2 space-y-8">
            <div>
              <h2 className="font-extrabold uppercase text-lg mb-3">About</h2>
              <p className="text-midnight-black/80 font-medium leading-relaxed">{photographer.bio}</p>
              <div className="flex items-center gap-4 mt-4 flex-wrap">
                {normalizeUrl(photographer.website) && (
                  <a href={normalizeUrl(photographer.website)} target="_blank" rel="noreferrer" data-testid="photographer-website-link" className="flex items-center gap-1.5 text-sm font-bold text-stranger-orange hover:underline">
                    <Globe className="w-4 h-4" /> Website
                  </a>
                )}
                {socialUrl(photographer.instagram, "instagram.com") && (
                  <a href={socialUrl(photographer.instagram, "instagram.com")} target="_blank" rel="noreferrer" data-testid="photographer-instagram-link" className="flex items-center gap-1.5 text-sm font-bold text-stranger-orange hover:underline">
                    <Instagram className="w-4 h-4" /> Instagram
                  </a>
                )}
                {socialUrl(photographer.facebook, "facebook.com") && (
                  <a href={socialUrl(photographer.facebook, "facebook.com")} target="_blank" rel="noreferrer" data-testid="photographer-facebook-link" className="flex items-center gap-1.5 text-sm font-bold text-stranger-orange hover:underline">
                    <Facebook className="w-4 h-4" /> Facebook
                  </a>
                )}
                {socialUrl(photographer.tiktok, "tiktok.com") && (
                  <a href={socialUrl(photographer.tiktok, "tiktok.com")} target="_blank" rel="noreferrer" data-testid="photographer-tiktok-link" className="flex items-center gap-1.5 text-sm font-bold text-stranger-orange hover:underline">
                    <Music2 className="w-4 h-4" /> TikTok
                  </a>
                )}
              </div>
            </div>

            {photographer.portfolio_urls?.length > 0 && (
              <div>
                <h2 className="font-extrabold uppercase text-lg mb-4">Portfolio</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="photographer-portfolio-grid">
                  {photographer.portfolio_urls.map((url, i) => (
                    <img key={i} src={mediaUrl(url)} alt={`${photographer.business_name} portfolio ${i + 1}`} className="w-full h-32 sm:h-40 object-cover rounded-xl" data-testid={`portfolio-image-${i}`} />
                  ))}
                </div>
              </div>
            )}

            {photographer.reviews?.length > 0 && (
              <div>
                <h2 className="font-extrabold uppercase text-lg mb-4">Reviews</h2>
                <div className="space-y-4">
                  {photographer.reviews.map((r) => (
                    <div key={r.id} className="border border-soft-gray rounded-xl p-4" data-testid={`review-${r.id}`}>
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: r.rating }).map((_, i) => (
                          <Star key={i} className="w-3.5 h-3.5 text-stranger-orange fill-stranger-orange" />
                        ))}
                      </div>
                      <p className="text-sm font-medium text-midnight-black/80">{r.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <h2 className="font-extrabold uppercase text-lg mb-4" id="upcoming-sessions">Upcoming Sessions</h2>
            {photographer.upcoming_sessions?.length ? (
              <div className="space-y-6">
                {photographer.upcoming_sessions.map((s) => (
                  <SessionCard key={s.id} session={{ ...s, photographer_name: photographer.business_name, photographer_verified: photographer.verified }} />
                ))}
              </div>
            ) : (
              <p className="text-sm font-medium text-midnight-black/60">No upcoming sessions right now. Check back soon.</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
